import { Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule, TableLazyLoadEvent } from 'primeng/table';
import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { TabsModule } from 'primeng/tabs';
import { catchError, of } from 'rxjs';
import {
  EmailNotificationLog,
  NotificationHealthSummary,
  ScheduledJobRun,
  UpcomingReminder
} from '../../models/notification-health.model';
import { NotificationHealthService } from '../../services/notification-health.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';

/**
 * Shows whether the notification machinery is actually running.
 *
 * Exists because a contract reminder outage ran for roughly five weeks unnoticed: the email log
 * has no UI and is excluded from the audit log, so "is this working?" could only be answered with
 * hand-written SQL. The health banner answers it at a glance.
 */
@Component({
  selector: 'app-notification-health',
  standalone: true,
  imports: [CommonModule, FormsModule, TableModule, Select, DatePicker, Tag, Tooltip, TabsModule],
  templateUrl: './notification-health.component.html',
  styleUrl: './notification-health.component.scss'
})
export class NotificationHealthComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);

  summary: NotificationHealthSummary | null = null;
  summaryLoading = false;

  // --- Job runs tab ---
  runs: ScheduledJobRun[] = [];
  runsLoading = false;
  runsTotal = 0;
  runsRows = 25;
  runsFirst = 0;
  runStatus: string | null = null;
  runsSortField: string | null = null;
  runsSortOrder = -1;

  // --- Email log tab ---
  emails: EmailNotificationLog[] = [];
  emailsLoading = false;
  emailsTotal = 0;
  emailsRows = 25;
  emailsFirst = 0;
  emailType: string | null = null;
  emailOutcome: boolean | null = null;
  emailFrom: Date | null = null;
  emailTo: Date | null = null;
  emailSearch = '';
  emailsSortField: string | null = null;
  emailsSortOrder = -1;
  notificationTypes: { label: string; value: string | null }[] = [];

  // --- Failed sends tab ---
  // Its own state, not a filtered view of `emails`. Sharing one array meant the Failed tab
  // rendered whatever the Email Log had last loaded, labelled as failures.
  failed: EmailNotificationLog[] = [];
  failedLoading = false;
  failedTotal = 0;
  failedRows = 25;
  failedFirst = 0;
  failedSortField: string | null = null;
  failedSortOrder = -1;

  // --- Upcoming tab ---
  upcoming: UpcomingReminder[] = [];
  upcomingLoading = false;
  daysAhead = 30;

  /** Selected tab, as p-tabs' string value. '0' Job Runs, '1' Email Log, '2' Upcoming, '3' Failed. */
  activeTab = '0';

  readonly rowsPerPageOptions = [10, 25, 50, 100];

  readonly statusOptions = [
    { label: 'All statuses', value: null },
    { label: 'Succeeded', value: 'Succeeded' },
    { label: 'Failed', value: 'Failed' },
    { label: 'Running', value: 'Running' }
  ];

  readonly outcomeOptions = [
    { label: 'All outcomes', value: null },
    { label: 'Sent', value: true },
    { label: 'Failed', value: false }
  ];

  readonly daysAheadOptions = [
    { label: 'Next 14 days', value: 14 },
    { label: 'Next 30 days', value: 30 },
    { label: 'Next 90 days', value: 90 }
  ];

  constructor(
    private service: NotificationHealthService,
    public pageHeaderService: PageHeaderSearchActionService
  ) {}

  ngOnInit(): void {
    // Filters live behind the header's funnel icon, as on the other list screens.
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search emails...');
    this.pageHeaderService.searchText
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((text) => {
        this.emailSearch = text ?? '';
        this.emailsFirst = 0;
        this.failedFirst = 0;
        this.loadEmails();
        this.loadFailed();
      });

    this.loadSummary();
    this.loadRuns();
    this.loadEmails();
    this.loadFailed();
    this.loadUpcoming();
    this.loadNotificationTypes();
  }

  ngOnDestroy(): void {
    this.pageHeaderService.setCanShowFilterToggle(false);
    this.pageHeaderService.setPlaceholder('');
  }

  // ---------------------------------------------------------------- summary

  loadSummary(): void {
    this.summaryLoading = true;
    this.service
      .getSummary()
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        this.summary = result;
        this.summaryLoading = false;
      });
  }

  // --------------------------------------------------------------- job runs

  onRunsLazyLoad(event: TableLazyLoadEvent): void {
    this.runsFirst = event.first ?? 0;
    this.runsRows = event.rows ?? this.runsRows;

    // Sorting is server-side: the table only holds one page, so sorting it client-side would
    // reorder that page alone and quietly lie about the rest.
    this.runsSortField = (event.sortField as string) ?? null;
    this.runsSortOrder = event.sortOrder ?? -1;

    this.loadRuns();
  }

  onRunsFilterChange(): void {
    this.runsFirst = 0;
    this.loadRuns();
  }

  loadRuns(): void {
    this.runsLoading = true;
    this.service
      .queryJobRuns({
        pageNumber: Math.floor(this.runsFirst / this.runsRows) + 1,
        pageSize: this.runsRows,
        status: this.runStatus ?? undefined,
        sortField: this.runsSortField ?? undefined,
        sortOrder: this.runsSortOrder
      })
      .pipe(
        catchError(() =>
          of({ items: [], totalCount: 0, pageNumber: 1, pageSize: this.runsRows })
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        this.runs = result.items;
        this.runsTotal = result.totalCount;
        this.runsLoading = false;
      });
  }

  // -------------------------------------------------------------- email log

  onEmailsLazyLoad(event: TableLazyLoadEvent): void {
    this.emailsFirst = event.first ?? 0;
    this.emailsRows = event.rows ?? this.emailsRows;

    // Server-side, for the same reason as the job-run table above.
    this.emailsSortField = (event.sortField as string) ?? null;
    this.emailsSortOrder = event.sortOrder ?? -1;

    this.loadEmails();
  }

  // Type and date range are shared by the Email Log and Failed Sends tabs, so both reload.
  onEmailsFilterChange(): void {
    this.emailsFirst = 0;
    this.failedFirst = 0;
    this.loadEmails();
    this.loadFailed();
  }

  loadEmails(): void {
    this.emailsLoading = true;
    this.service
      .queryEmailLog({
        pageNumber: Math.floor(this.emailsFirst / this.emailsRows) + 1,
        pageSize: this.emailsRows,
        notificationType: this.emailType ?? undefined,
        // Pass through as-is: false means "failed only" and must not be dropped.
        isSent: this.emailOutcome === null ? undefined : this.emailOutcome,
        fromDate: this.toIsoDate(this.emailFrom),
        toDate: this.toIsoDate(this.emailTo),
        search: this.emailSearch || undefined,
        sortField: this.emailsSortField ?? undefined,
        sortOrder: this.emailsSortOrder
      })
      .pipe(
        catchError(() =>
          of({ items: [], totalCount: 0, pageNumber: 1, pageSize: this.emailsRows })
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        this.emails = result.items;
        this.emailsTotal = result.totalCount;
        this.emailsLoading = false;
      });
  }

  // ------------------------------------------------------------ failed sends

  onFailedLazyLoad(event: TableLazyLoadEvent): void {
    this.failedFirst = event.first ?? 0;
    this.failedRows = event.rows ?? this.failedRows;
    this.failedSortField = (event.sortField as string) ?? null;
    this.failedSortOrder = event.sortOrder ?? -1;
    this.loadFailed();
  }

  loadFailed(): void {
    this.failedLoading = true;
    this.service
      .queryEmailLog({
        pageNumber: Math.floor(this.failedFirst / this.failedRows) + 1,
        pageSize: this.failedRows,
        notificationType: this.emailType ?? undefined,
        // Always failures — that is what this tab is.
        isSent: false,
        fromDate: this.toIsoDate(this.emailFrom),
        toDate: this.toIsoDate(this.emailTo),
        search: this.emailSearch || undefined,
        sortField: this.failedSortField ?? undefined,
        sortOrder: this.failedSortOrder
      })
      .pipe(
        catchError(() =>
          of({ items: [], totalCount: 0, pageNumber: 1, pageSize: this.failedRows })
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        this.failed = result.items;
        this.failedTotal = result.totalCount;
        this.failedLoading = false;
      });
  }

  loadNotificationTypes(): void {
    this.service
      .getNotificationTypes()
      .pipe(
        catchError(() => of([] as string[])),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((types) => {
        this.notificationTypes = [
          { label: 'All types', value: null },
          ...types.map((t) => ({ label: t, value: t }))
        ];
      });
  }

  // -------------------------------------------------------------------- tabs

  onTabChange(value: string | number | undefined): void {
    this.activeTab = String(value ?? '0');
  }

  // ----------------------------------------------------------------- filters

  clearFilters(): void {
    this.runStatus = null;
    this.emailType = null;
    this.emailOutcome = null;
    this.emailFrom = null;
    this.emailTo = null;
    this.daysAhead = 30;

    this.runsFirst = 0;
    this.emailsFirst = 0;
    this.failedFirst = 0;

    this.loadRuns();
    this.loadEmails();
    this.loadFailed();
    this.loadUpcoming();
  }

  /** Whether the clear button should show, judged against the active tab's own filters. */
  hasActiveFilters(): boolean {
    if (this.activeTab === '0') {
      return this.runStatus !== null;
    }
    if (this.activeTab === '2') {
      return this.daysAhead !== 30;
    }
    if (this.activeTab === '3') {
      // Outcome is not a filter here — the tab IS failures.
      return this.emailType !== null || this.emailFrom !== null || this.emailTo !== null;
    }
    return (
      this.emailType !== null ||
      this.emailOutcome !== null ||
      this.emailFrom !== null ||
      this.emailTo !== null
    );
  }

  // ---------------------------------------------------------------- upcoming

  loadUpcoming(): void {
    this.upcomingLoading = true;
    this.service
      .getUpcoming(this.daysAhead)
      .pipe(
        catchError(() => of([] as UpcomingReminder[])),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        this.upcoming = result;
        this.upcomingLoading = false;
      });
  }

  // ----------------------------------------------------------------- helpers

  /** Date-only ISO (yyyy-MM-dd) built from local parts, so a date filter is not shifted a day by UTC conversion. */
  private toIsoDate(value: Date | null): string | undefined {
    if (!value) {
      return undefined;
    }
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${value.getFullYear()}-${month}-${day}`;
  }

  /** Banner heading. "Pending" is a setup state, not a failure, and is worded as one. */
  get bannerTitle(): string {
    switch (this.summary?.severity) {
      case 'Ok':
        return 'Reminders are running';
      case 'Pending':
        return 'Waiting for the first scheduled run';
      default:
        return 'Reminders have stopped';
    }
  }

  get bannerIcon(): string {
    return this.summary?.severity === 'Ok' ? 'pi-check-circle' : 'pi-exclamation-triangle';
  }

  runSeverity(status: string): 'success' | 'danger' | 'warn' | 'info' {
    switch (status) {
      case 'Succeeded':
        return 'success';
      case 'Failed':
        return 'danger';
      case 'Running':
        return 'warn';
      default:
        return 'info';
    }
  }

  /** Urgency of an upcoming reminder, so a contract about to expire stands out. */
  upcomingSeverity(r: UpcomingReminder): 'success' | 'danger' | 'warn' | 'info' {
    if (r.alreadySent) {
      return 'success';
    }
    return r.daysUntilExpiry <= 14 ? 'danger' : r.daysUntilExpiry <= 30 ? 'warn' : 'info';
  }

  formatDuration(seconds: number | null): string {
    if (seconds === null) {
      return '—';
    }
    if (seconds < 60) {
      return `${seconds.toFixed(1)}s`;
    }
    return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  }

  /** Recipient lists can be long; show the first few and a count. */
  shortRecipients(recipients: string): string {
    const list = recipients.split(/[;,]/).map((r) => r.trim()).filter(Boolean);
    if (list.length <= 2) {
      return list.join(', ');
    }
    return `${list.slice(0, 2).join(', ')} +${list.length - 2} more`;
  }
}
